import { useCallback } from 'react';
import { useModal } from 'context';

import HorizontalBar from './HorizontalBar';
import RangeBar, { Range } from './RangeBar';

import { Tip } from './utils';

import arrowDown from 'assets/images/arrow-down.svg';

import './style.css';

interface AdviceItemProps {
  text?: string;
  rate?: number;
  unit?: string;
  title: string;
  score: number | undefined;
  range?: Range;
  header?: string;
  advice: Tip[];
  position: number;
  definition?: JSX.Element;
  activePosition: number | null;
  backgroundColor: string;
  setActivePosition: React.Dispatch<React.SetStateAction<number | null>>;
}

const AdviceItem = ({
  text,
  rate,
  unit,
  title,
  score,
  range,
  header,
  advice,
  position,
  definition,
  activePosition,
  backgroundColor,
  setActivePosition,
}: AdviceItemProps) => {
  const { openModal, ModalWrapper } = useModal();

  const isNoScore = score === undefined;

  const isActive = position === activePosition && !isNoScore;

  const handleClick = useCallback(() => {
    setActivePosition(isActive ? null : position);
  }, [isActive, position, setActivePosition]);

  return (
    <div
      className="grid items-center"
      style={{
        gridTemplateRows: 'auto 1fr',
        gridTemplateColumns: '38px 1fr'
      }}
    >
      <button
        onClick={handleClick}
        disabled={isNoScore}
      >
        <img
          className={`${isActive ? 'arrow-down__open' : 'arrow-down__closed'}`}
          width={16}
          height={16}
          src={arrowDown}
          alt={`${title} open/close controller`}
        />
      </button>
      <div className="flex flex-wrap sm:flex-nowrap">
        <button
          className="text-left"
          style={{
            minWidth: '167px',
            color: '#201F1E',
            fontWeight: 600,
          }}
          onClick={handleClick}
          disabled={isNoScore}
        >
          {title}
        </button>
        <HorizontalBar
          score={score}
          backgroundColor={backgroundColor}
        />
      </div>
      <div
        className={`
          col-start-2 flex flex-col advice-item
          ${isActive ? 'mt-4 active-item__open' : 'h-0 opacity-0'}
        `}
      >
        {range && (
          <>
            <span>
              {header}
            </span>
            <RangeBar
              rate={rate}
              unit={unit}
              range={range}
              backgroundColor={backgroundColor}
            />
          </>
        )}
        {
          text && (
            <span className="pb-0.5">
              {text}
            </span>
          )
        }
        {
          advice?.length > 1
            ? (
              <ul className="xs:pl-6 list-disc grid gap-0.5">
                {advice.map(({ key, text }) => (
                  <li key={key}>
                    {text}
                  </li>
                ))}
              </ul>
            )
            : advice?.length === 1
              ? (
                <span>
                  {advice[0].text}
                </span>
              )
            : <></>
        }
        {definition && (
          <div className="mt-1 flex">
            <button
              className="hover:underline underline-offset-2"
              style={{ color: '#5B5FC7' }}
              onClick={() => {
                openModal(
                  <ModalWrapper
                    title={title}
                    childrenClassName="w-112 max-w-full min-h-64"
                  >
                    <div className="flex flex-col gap-3">
                      {definition}
                    </div>
                  </ModalWrapper>
                )
              }}
            >
              Learn more
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdviceItem;
